import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { useUserStats, useCategories } from "@/hooks/useExam";
import { getMyAccessStatus } from "@/api/subscriptions";

const TIPS = [
  {
    img: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop&auto=format&q=60",
    title: "Study a little every day",
    body: "Short daily sessions beat cramming. Aim for 20–30 minutes per day across 4–6 weeks before your test date.",
  },
  {
    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop&auto=format&q=60",
    title: "Focus on weak categories",
    body: "Use our category practice to drill the topics where you're losing marks — hazard awareness and road signs trip most people up.",
  },
  {
    img: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&h=400&fit=crop&auto=format&q=60",
    title: "Simulate test conditions",
    body: "Take full 50-question timed mock exams with no distractions. The real test is 57 minutes — practise until the timer feels comfortable.",
  },
];

const FAQS = [
  {
    q: "How many questions are in the UK theory test?",
    a: "The multiple-choice part has 50 questions. You need 43 correct answers (86%) to pass. The test also includes a separate Hazard Perception section.",
  },
  {
    q: "How long does the theory test take?",
    a: "You get 57 minutes for the multiple-choice section. The Hazard Perception video clips take around 20 minutes on top of that.",
  },
  {
    q: "How much does the theory test cost?",
    a: "The DVSA charges £23 to sit the theory test. You can book online at gov.uk/book-theory-test.",
  },
  {
    q: "How long is my theory test pass valid?",
    a: "Your theory test pass certificate is valid for 2 years. You must pass your practical test within that window, otherwise you'll need to re-sit the theory test.",
  },
  {
    q: "What should I bring to the test centre?",
    a: "You must bring your UK photocard driving licence. No other ID is accepted. Arrive 15 minutes early and leave your phone in the car.",
  },
  {
    q: "How many times can I fail the theory test?",
    a: "There's no limit to how many times you can sit it — but each attempt costs £23. You must wait 3 clear working days before retaking.",
  },
];

const ChecklistIcon = ({ path }: { path: string }) => (
  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  </div>
);

const CHECKLIST = [
  {
    iconPath: "M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z",
    title: "Photocard driving licence",
    desc: "The only accepted ID. No passport, no paper counterpart — just your photocard.",
    badge: "Required",
  },
  {
    iconPath: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z",
    title: "Arrive 15 minutes early",
    desc: "Late arrivals may not be admitted. Allow time for parking and signing in.",
    badge: null,
  },
  {
    iconPath: "M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3m-3 3h3m-6-3h.008v.008H7.5V12zm0 3h.008v.008H7.5V15z",
    title: "No phone in the room",
    desc: "Leave it in your car or a locker. Found with one? You'll be disqualified.",
    badge: "Required",
  },
  {
    iconPath: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178zM15 12a3 3 0 11-6 0 3 3 0 016 0z",
    title: "Glasses or contacts",
    desc: "Bring vision correction if you need it. You must pass the number-plate sight test.",
    badge: null,
  },
  {
    iconPath: "M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z",
    title: "Booking confirmation",
    desc: "Have your booking reference ready — printed or on your phone before entering.",
    badge: null,
  },
  {
    iconPath: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    title: "Stay calm & read carefully",
    desc: "Flag tricky questions and revisit them. Every word in the question matters.",
    badge: null,
  },
];

const TEST_FACTS = [
  {
    iconPath: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
    text: "50 multiple-choice questions from the official DVSA question bank",
  },
  {
    iconPath: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z",
    text: "57 minutes to answer all questions — you can flag and revisit any question",
  },
  {
    iconPath: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    text: "Pass mark is 43 out of 50 (86%)",
  },
  {
    iconPath: "M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z",
    text: "14 hazard perception clips scored separately (pass mark 44/75)",
  },
  {
    iconPath: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5",
    text: "Certificate valid for 2 years — must pass practical within this window",
  },
  {
    iconPath: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75",
    text: "£23 fee per attempt — book at gov.uk/book-theory-test",
  },
];

function LoggedInHero({ user }: { user: { first_name: string | null } }) {
  const { data: stats } = useUserStats();
  const { data: accessStatus } = useQuery({
    queryKey: ["access-status"],
    queryFn: getMyAccessStatus,
  });

  const showFreeTrial = accessStatus && !accessStatus.has_used_free_trial && !accessStatus.has_active_subscription;
  const showSubscribeBanner = accessStatus && accessStatus.has_used_free_trial && !accessStatus.has_active_subscription;

  return (
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
      <div className="max-w-3xl">
        <h1 className="text-3xl sm:text-4xl font-bold text-white">
          Welcome back{user.first_name ? `, ${user.first_name}` : ""}
        </h1>
        <p className="mt-3 text-primary-100">Keep practising — you're making progress.</p>

        {/* Stats row */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Attempts", value: stats.completed_attempts },
              { label: "Avg score", value: stats.average_score != null ? `${Math.round(stats.average_score)}%` : "—" },
              { label: "Pass rate", value: stats.pass_rate != null ? `${Math.round(stats.pass_rate)}%` : "—" },
              { label: "Best score", value: stats.best_score != null ? `${Math.round(stats.best_score)}%` : "—" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-white/10 ring-1 ring-white/20 p-4 text-center">
                <div className="text-2xl font-bold text-white">{s.value}</div>
                <div className="mt-0.5 text-xs text-primary-200">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Free trial CTA */}
        {showFreeTrial && (
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/free-trial"
              className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-800 hover:bg-primary-50 transition-colors"
            >
              🎁 Start Free Mock Exam
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-lg border-2 border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
            >
              View Plans
            </Link>
          </div>
        )}

        {/* Subscribe CTA */}
        {showSubscribeBanner && (
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-800 hover:bg-primary-50 transition-colors"
            >
              Subscribe to continue practising →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function GuestHero() {
  return (
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white ring-1 ring-white/20 mb-6">
          {/* Steering wheel icon */}
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 0v4m0 12v-4m0-8a4 4 0 100 8 4 4 0 000-8zm-8 4h4m8 0h-4" />
          </svg>
          UK Theory Test Preparation
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
          Pass Your Driving Theory Test
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-primary-100 leading-relaxed">
          Realistic mock exams, category practice, and full explanations — everything you need to pass first time.
        </p>
        {/* Social proof */}
        <div className="mt-10 flex items-center gap-6">
          <div className="flex -space-x-2">
            {["1027057", "1239291", "774909", "1043471"].map((id) => (
              <img
                key={id}
                src={`https://randomuser.me/api/portraits/men/${id.slice(-2)}.jpg`}
                alt="user"
                className="h-8 w-8 rounded-full ring-2 ring-primary-800 object-cover bg-primary-700"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ))}
          </div>
          <p className="text-sm text-primary-200">
            <span className="font-semibold text-white">1,000+</span> practice questions available
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { isAuthenticated, user } = useAuthStore();

  return (
    <div className="page-enter">

      {/* Hero */}
      <section className="relative min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506905489585-095b1afc9edd?w=1600&h=700&fit=crop&auto=format&q=70"
            alt="UK motorway"
            className="h-full w-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 via-primary-800/75 to-transparent" />
        </div>

        {isAuthenticated && user ? (
          <LoggedInHero user={user} />
        ) : (
          <GuestHero />
        )}
      </section>

      {/* Category preview — always shown */}
      <CategoryPreview />

      {/* About the real test */}
      <section className="bg-gray-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">About the UK Theory Test</h2>
              <p className="mt-4 text-gray-500 leading-relaxed">
                The DVSA theory test is split into two parts: <strong>multiple-choice questions</strong> and <strong>hazard perception</strong>. You must pass both on the same day to receive your certificate.
              </p>
              <ul className="mt-6 space-y-4">
                {TEST_FACTS.map((item) => (
                  <li key={item.text} className="flex items-start gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={item.iconPath} />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-600">{item.text}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://www.gov.uk/book-theory-test"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
              >
                Book your test at gov.uk
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800&h=600&fit=crop&auto=format&q=70"
                alt="Learner driver in a car"
                className="h-full w-full object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.src = "";
                  el.parentElement!.classList.add("bg-primary-100");
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <p className="text-white text-sm font-medium">Over 1.6 million theory tests are taken in the UK each year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Test day checklist */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Test Day Checklist</h2>
            <p className="mt-3 text-gray-500 max-w-xl mx-auto">Don't let avoidable mistakes derail your test day.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CHECKLIST.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-xl border border-gray-100 bg-gray-50 p-5">
                <ChecklistIcon path={item.iconPath} />
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                    {item.badge && (
                      <span className="rounded-full bg-danger-100 px-2 py-0.5 text-xs font-bold text-danger-700">{item.badge}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study tips */}
      <section className="bg-gray-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Study Tips</h2>
            <p className="mt-3 text-gray-500">How to make the most of your practice time.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TIPS.map((tip) => (
              <div key={tip.title} className="rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="aspect-[16/9] overflow-hidden bg-primary-100">
                  <img
                    src={tip.img}
                    alt={tip.title}
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{tip.title}</h3>
                  <p className="mt-2 text-sm text-gray-500 leading-relaxed">{tip.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <dl className="space-y-3">
            {FAQS.map((faq) => (
              <details key={faq.q} className="group rounded-xl border border-gray-200 bg-gray-50 px-6 py-4 cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 list-none select-none">
                  <span>{faq.q}</span>
                  <svg
                    className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-180"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA — only for guests */}
      {!isAuthenticated && (
        <section className="relative overflow-hidden bg-primary-800 py-20">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            }}
          />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white">Ready to Start Practising?</h2>
            <p className="mt-3 text-lg text-primary-200 max-w-2xl mx-auto">
              Free mock exams and category practice. No credit card needed.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-lg bg-white px-8 py-3.5 text-base font-semibold text-primary-800 shadow-lg hover:bg-primary-50 transition-colors"
              >
                Create Free Account
              </Link>
              <Link
                to="/exams"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white/30 px-8 py-3.5 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Browse Mock Tests
              </Link>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}

function CategoryPreview() {
  const { data: categories } = useCategories();
  if (!categories || categories.length === 0) return null;

  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Practice by Category</h2>
            <p className="mt-2 text-gray-500">14 topics covering every area of the UK theory test.</p>
          </div>
          <Link to="/categories" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-500">
            View all
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {categories.slice(0, 10).map((cat) => (
            <Link
              key={cat.id}
              to="/categories"
              className="rounded-xl border border-gray-200 bg-gray-50 p-4 hover:border-primary-300 hover:bg-primary-50 transition-all group"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white shadow-sm text-primary-600 mb-3 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                <svg className="h-4.5 w-4.5 h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-800 group-hover:text-primary-700 leading-snug">{cat.name}</p>
              <p className="mt-1 text-xs text-gray-400">{cat.question_count} questions</p>
            </Link>
          ))}
          {categories.length > 10 && (
            <Link
              to="/categories"
              className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 hover:border-primary-300 hover:bg-primary-50 transition-all flex flex-col items-center justify-center text-center"
            >
              <p className="text-sm font-semibold text-gray-500">+{categories.length - 10} more</p>
              <p className="mt-1 text-xs text-gray-400">View all</p>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
